import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import buildReviewPath from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/Helpers/buildReviewPath';
import GenerationSweepBar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/GeneratedDocumentRow/GenerationSweepBar';
import describeElementsSource from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/GeneratedDocumentRow/Helpers/describeElementsSource';
import RetryDocumentButton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/GenerateSection/StageCard/GeneratedDocumentRow/RetryDocumentButton';
import type WorkflowRunDocumentView from '@UI/Store/WorkflowRuns/Views/WorkflowRunDocumentView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@verclam/ui-kit';

const rowStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

const headStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	flex-wrap: wrap;
`;

const labelStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	font-size: 1.0625rem;
	color: ${theme.colors.ink};
`;

const CHIP_COLORS: Readonly<Record<string, { background: string; color: string }>> = Object.freeze({
	running: { background: theme.colors.accentSurface, color: theme.colors.accentDark },
	to_review: { background: theme.states.warning.surface, color: theme.tints.amber.ink },
	verified: { background: theme.states.success.surface, color: theme.states.success.ink },
	failed: { background: theme.states.danger.surface, color: theme.states.danger.solid },
});

const NEUTRAL_CHIP_COLORS = {
	background: theme.colors.surfaceMuted,
	color: theme.colors.inkSecondary,
} as const;

const chipStyle = (stateValue: string) => css`
	padding: 0.375rem 0.6875rem;
	border-radius: ${theme.radii.pill};
	background-color: ${(CHIP_COLORS[stateValue] ?? NEUTRAL_CHIP_COLORS).background};
	color: ${(CHIP_COLORS[stateValue] ?? NEUTRAL_CHIP_COLORS).color};
	font-family: ${theme.fonts.body};
	font-size: 0.71875rem;
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.2;
	white-space: nowrap;
`;

const lineStyle = (isFailure: boolean) => css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	line-height: 1.6;
	color: ${isFailure ? theme.states.danger.solid : theme.colors.inkSecondary};
`;

const actionsStyle = css`
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	/** 0-indexed stage the document belongs to, carried to the review screen. */
	readonly stage: number;
	readonly document: WorkflowRunDocumentView;
	/** Number of elements of the preparation, named in the line shown while a document generates. */
	readonly elementCount: number;
	/** Whether the visitor may launch (« Réessayer »): permission, dossier not archived. */
	readonly canLaunch: boolean;
};

/**
 * One document of a stage: its title and server state, the failure message when it failed, its source line and a sweep
 * bar while it generates, « Réessayer » when the server allows it and « Relire et corriger » once there
 * is a document to read.
 */
const GeneratedDocumentRow: React.FC<Props> = ({
	subject,
	preparationId,
	stage,
	document,
	elementCount,
	canLaunch,
}) => {
	const navigate = useNavigate();
	const { documentTypes } = useAccountDocumentTypes();
	const reviewLabel = document.isToReview ? 'Relire et corriger' : 'Relire';
	const documentId = document.documentId;

	const handleReviewClick = React.useCallback(() => {
		if (documentId !== null) {
			navigate(buildReviewPath(subject, documentId, preparationId, stage));
		}
	}, [navigate, subject, documentId, preparationId, stage]);

	const isRunning = document.state.value === 'running';
	const retryStepRunId = document.canRetry && canLaunch ? document.stepRunId : null;

	return (
		<li css={rowStyle}>
			<div css={headStyle}>
				<h3 css={labelStyle}>{documentTypes?.labelOf(document.documentTypeId) ?? 'Document'}</h3>
				<span css={chipStyle(document.state.value)}>{document.state.label}</span>
			</div>
			<ConditionalLoader data={document.failureMessage}>
				{(failureMessage) => <p css={lineStyle(true)}>{failureMessage}</p>}
			</ConditionalLoader>
			<ConditionalLoader data={isRunning}>
				<p css={lineStyle(false)}>Génération à partir {describeElementsSource(elementCount)}…</p>
				<GenerationSweepBar />
			</ConditionalLoader>
			<ConditionalLoader data={retryStepRunId !== null || documentId !== null}>
				<div css={actionsStyle}>
					<ConditionalLoader data={retryStepRunId}>
						{(stepRunId) => (
							<RetryDocumentButton
								subject={subject}
								preparationId={preparationId}
								stepRunId={stepRunId}
							/>
						)}
					</ConditionalLoader>
					<ConditionalLoader data={documentId !== null}>
						<Button
							type="button"
							size="sm"
							severity={document.isToReview ? undefined : 'secondary'}
							label={reviewLabel}
							onClick={handleReviewClick}
						/>
					</ConditionalLoader>
				</div>
			</ConditionalLoader>
		</li>
	);
};

export default GeneratedDocumentRow;
